/**
 * Utility functions for date handling and data freshness
 */

/**
 * Check if a given month is the current month and incomplete
 * @param monthString - Month string in format "YYYY-MM"
 * @returns true if the month is current and incomplete, false otherwise
 */
export function isIncompleteCurrentMonth(monthString: string): boolean {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 0-indexed
  
  const [year, month] = monthString.split('-').map(Number);
  
  // Check if it's the current month
  if (year === currentYear && month === currentMonth) {
    // Check if we're not at the last day of the month
    const lastDayOfMonth = new Date(currentYear, currentMonth, 0).getDate();
    const currentDay = now.getDate();
    
    return currentDay < lastDayOfMonth;
  }
  
  return false;
}

/**
 * Get the last complete month in YYYY-MM format
 * @returns Last complete month string
 */
export function getLastCompleteMonth(): string {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  
  const year = lastMonth.getFullYear();
  const month = String(lastMonth.getMonth() + 1).padStart(2, '0');
  
  return `${year}-${month}`;
}

/**
 * Get the last complete month as a Date object
 * @returns Date object for the first day of last complete month
 */
export function getLastCompleteMonthDate(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() - 1, 1);
}

/**
 * Format a date string for display
 * @param dateString - Date string in format "YYYY-MM-DD" or "YYYY-MM"
 * @returns Formatted date string like "October 2025" or "Oct 31, 2025"
 */
export function formatMonthYear(dateString: string): string {
  const parts = dateString.split('-');
  const year = parseInt(parts[0]);
  const month = parseInt(parts[1]) - 1;
  
  const date = new Date(year, month, 1);
  
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Get last available data date message
 * @param monthString - Month string in format "YYYY-MM"
 * @returns Message about data freshness
 */
export function getDataFreshnessMessage(monthString: string): string {
  const isIncomplete = isIncompleteCurrentMonth(monthString);
  
  if (isIncomplete) {
    const now = new Date();
    
    return `Data current through ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  
  return `Complete data for ${formatMonthYear(monthString)}`;
}


